import cx from "classnames";
import { ReactElement, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import arrowCurved from "src/app/assets/icons/onboarding-arrow-curved.svg";
import arrowDown from "src/app/assets/icons/onboarding-arrow-down.svg";

import styles from "./index.module.scss";

type Props = {
  onClose: () => void;
};

type TooltipPosition = {
  archive: { top: number; right: number } | null;
  startReading: { top: number; left: number } | null;
};

export function OnboardingOverlay({ onClose }: Props): ReactElement {
  const { t } = useTranslation("onboarding");
  const [positions, setPositions] = useState<TooltipPosition>({
    archive: null,
    startReading: null,
  });

  const calculatePositions = useCallback(() => {
    const archiveIcon = document.querySelector(
      '[data-onboarding-highlight="archive"]',
    ) as HTMLElement | null;
    const startReadingBtn = document.querySelector(
      '[data-onboarding-highlight="start-reading"]',
    ) as HTMLElement | null;

    if (archiveIcon) {
      const rect = archiveIcon.getBoundingClientRect();
      setPositions((prev) => ({
        ...prev,
        archive: {
          top: rect.bottom + 10,
          // Offset tooltip left so arrow points near the icon
          right: window.innerWidth - rect.right + 15,
        },
      }));
    }

    if (startReadingBtn) {
      const rect = startReadingBtn.getBoundingClientRect();
      setPositions((prev) => ({
        ...prev,
        startReading: {
          top: rect.top - 20, // Position above the button
          left: rect.left + rect.width / 2,
        },
      }));
    }
  }, []);

  useEffect(() => {
    // Find elements to highlight and apply styles
    const archiveIcon = document.querySelector(
      '[data-onboarding-highlight="archive"]',
    ) as HTMLElement | null;
    const startReadingBtn = document.querySelector(
      '[data-onboarding-highlight="start-reading"]',
    ) as HTMLElement | null;

    const elementsToHighlight: HTMLElement[] = [];

    if (archiveIcon) {
      elementsToHighlight.push(archiveIcon);
      archiveIcon.style.position = "relative";
      archiveIcon.style.zIndex = "102";
      archiveIcon.style.backgroundColor = "#fff";
      archiveIcon.style.borderRadius = "40px";
      archiveIcon.style.padding = "8px";
      archiveIcon.style.boxShadow = "0 0 0 2px #000";
    }

    if (startReadingBtn) {
      elementsToHighlight.push(startReadingBtn);
      startReadingBtn.style.position = "relative";
      startReadingBtn.style.zIndex = "102";
      startReadingBtn.style.boxShadow = "0 0 0 2px #000";
    }

    // Calculate initial positions
    calculatePositions();

    // Recalculate on resize
    window.addEventListener("resize", calculatePositions);

    // Cleanup: restore original styles when overlay unmounts
    return () => {
      window.removeEventListener("resize", calculatePositions);
      elementsToHighlight.forEach((el) => {
        el.style.position = "";
        el.style.zIndex = "";
        el.style.backgroundColor = "";
        el.style.borderRadius = "";
        el.style.padding = "";
        el.style.boxShadow = "";
      });
    };
  }, [calculatePositions]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    onClose();
  };

  return (
    <div
      className={cx(styles.overlay)}
      onClick={handleOverlayClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " " || e.key === "Escape") {
          onClose();
        }
      }}
    >
      {/* VIEW ARCHIVE Tooltip */}
      {positions.archive && (
        <div
          className={cx(styles.tooltipArchive)}
          style={{
            top: positions.archive.top,
            right: positions.archive.right,
          }}
        >
          <img
            src={arrowCurved}
            alt=""
            className={cx(styles.arrowCurved)}
          />
          <div className={cx(styles.tooltipContent, styles.alignRight)}>
            <h3 className={cx(styles.tooltipTitle)}>
              {t("overlay.viewArchive.title")}
            </h3>
            <p className={cx(styles.tooltipText)}>
              {t("overlay.viewArchive.description")}
            </p>
          </div>
        </div>
      )}

      {/* START READING Tooltip */}
      {positions.startReading && (
        <div
          className={cx(styles.tooltipStartReading)}
          style={{
            top: positions.startReading.top,
            left: positions.startReading.left,
          }}
        >
          <div className={cx(styles.tooltipContent, styles.alignCenter)}>
            <h3 className={cx(styles.tooltipTitle)}>
              {t("overlay.startReading.title")}
            </h3>
            <p className={cx(styles.tooltipText)}>
              {t("overlay.startReading.description")}
            </p>
          </div>
          <img
            src={arrowDown}
            alt=""
            className={cx(styles.arrowDown)}
          />
        </div>
      )}
    </div>
  );
}
